import { ApiClient, PagedResults, ServiceResponse, ServiceResponseNoContent } from "../../../common";
import { PlatformSettings } from "../../../settings/platformSettings";
import { CreateMessageTemplateDto } from "../../dtos/in/createMessageTemplateDto";
import { SendEmailDto } from "../../dtos/in/sendEmailDto";
import { SendMessageDto } from "../../dtos/in/sendMessageDto";
import { SendNotificationDto } from "../../dtos/in/sendNotificationDto";
import { SendSmsDto } from "../../dtos/in/sendSmsDto";
import { SendVoiceMessageDto } from "../../dtos/in/sendVoiceMessageDto";
import { UpdateEmailTemplateDto } from "../../dtos/in/updateEmailTemplateDto";
import { UpdateMessageTemplateDto } from "../../dtos/in/updateMessageTemplateDto";
import { UpdateNotificationTemplateDto } from "../../dtos/in/updateNotificationTemplateDto";
import { UpdatePreferenceDto } from "../../dtos/in/updatePreferenceDto";
import { UpdateSmsTemplateDto } from "../../dtos/in/updateSmsTemplateDto";
import { UpdateVoiceMessageTemplateDto } from "../../dtos/in/updateVoiceMessageTemplateDto";
import { MessageDto } from "../../dtos/out/messageDto";
import { MessageTemplateDto } from "../../dtos/out/messageTemplateDto";
import { NotificationDto } from "../../dtos/out/notificationDto";
import { PreferenceDto } from "../../dtos/out/preferenceDto";
import { ReadStatus } from "../../enums/readStatus";
import { ICommsClient } from "../interfaces/commsClient";

class CommsClient extends ApiClient implements ICommsClient {

    private _baseUrl: string;
    constructor(private platformSettings: PlatformSettings) {
        super();
        this._baseUrl = platformSettings.apiUrl + "/comms";
    }

    get baseUrl(): string {
        return this._baseUrl;
    }

    public async sendMessage(dto: SendMessageDto, accessToken: string): Promise<ServiceResponse<MessageDto>> {
        return await super.Post<SendMessageDto, MessageDto>(this.baseUrl, `messages`, dto, accessToken);
    }

    public async setMessageStatus(messageId: string, readStatus: ReadStatus, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.PutNoResponse(this.baseUrl, `messages/${messageId}/status/${readStatus}`, null, accessToken);
    }

    public async getMessageById(messageId: string, accessToken: string): Promise<ServiceResponse<MessageDto>> {
        return await super.Get<MessageDto>(this.baseUrl, `messages/${messageId}`, accessToken);
    }

    public async getMessagesByUserId(userId: number, includeNewOnly: boolean, page: number, pageSize: number, accessToken: string): Promise<ServiceResponse<PagedResults<MessageDto>>> {
        return await super.Get<PagedResults<MessageDto>>(this.baseUrl, `users/${userId}/messages?includeNewOnly=${includeNewOnly}&page=${page}&pageSize=${pageSize}`, accessToken);
    }

    public async deleteMessage(messageId: string, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Delete(this.baseUrl, `messages/${messageId}`, accessToken);
    }

    public async getTemplateById(templateId: string, accessToken: string): Promise<ServiceResponse<MessageTemplateDto>> {
        return await super.Get<MessageTemplateDto>(this.baseUrl, `message-templates/${templateId}`, accessToken);
    }

    public async searchTemplates(page: number, pageSize: number, accessToken: string): Promise<ServiceResponse<PagedResults<MessageTemplateDto>>>{
        return await super.Get<PagedResults<MessageTemplateDto>>(this.baseUrl, `message-templates?page=${page}&pageSize=${pageSize}`, accessToken);
    }

    public async createTemplate(dto: CreateMessageTemplateDto, accessToken: string): Promise<ServiceResponse<MessageTemplateDto>> {
        return await super.Post<CreateMessageTemplateDto, MessageTemplateDto>(this.baseUrl, `message-templates`, dto, accessToken);
    }

    public async updateTemplate(templateId: string, dto: UpdateMessageTemplateDto, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.PutNoResponse<UpdateMessageTemplateDto>(this.baseUrl, `message-templates/${templateId}`, dto, accessToken);
    }

    public async deleteTemplate(templateId: string, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Delete(this.baseUrl, `message-templates/${templateId}`, accessToken);
    }

    public async updateEmailTemplate(templateId: string, dto: UpdateEmailTemplateDto, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.PutNoResponse<UpdateEmailTemplateDto>(this.baseUrl, `message-templates/${templateId}/email-template`, dto, accessToken);
    }

    public async updateSmsTemplate(templateId: string, dto: UpdateSmsTemplateDto, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.PutNoResponse<UpdateSmsTemplateDto>(this.baseUrl, `message-templates/${templateId}/sms-template`, dto, accessToken);
    }

    public async updateVoiceMessageTemplate(templateId: string, dto: UpdateVoiceMessageTemplateDto, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.PutNoResponse<UpdateVoiceMessageTemplateDto>(this.baseUrl, `message-templates/${templateId}/voice-message-template`, dto, accessToken);
    }

    public async updateNotificationTemplate(templateId: string, dto: UpdateNotificationTemplateDto, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.PutNoResponse<UpdateNotificationTemplateDto>(this.baseUrl, `message-templates/${templateId}/notification-template`, dto, accessToken);
    }

    public async getPreferencesByUserId(userId: number, accessToken: string): Promise<ServiceResponse<PreferenceDto[]>> {
        return await super.Get<PreferenceDto[]>(this.baseUrl, `users/${userId}/preferences`, accessToken);
    }

    public async getPreference(userId: number, templateId: string, accessToken: string): Promise<ServiceResponse<PreferenceDto>> {
        return await super.Get<PreferenceDto>(this.baseUrl, `users/${userId}/message-templates/${templateId}/preferences`, accessToken);
    }

    public async updatePreference(userId: number, templateId: string, dto: UpdatePreferenceDto, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.PutNoResponse(this.baseUrl, `users/${userId}/message-templates/${templateId}/preferences`, dto, accessToken);
    }

    public async deletePreference(userId: number, templateId: string, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Delete(this.baseUrl, `users/${userId}/message-templates/${templateId}/preferences`, accessToken);
    }

    public async sendNotification(dto: SendNotificationDto, accessToken: string): Promise<ServiceResponse<NotificationDto>> {
        return await super.Post<SendNotificationDto, NotificationDto>(this.baseUrl, `notifications`, dto, accessToken);
    }

    public async getNotificationById(notificationId: string, accessToken: string): Promise<ServiceResponse<NotificationDto>> {
        return await super.Get<NotificationDto>(this.baseUrl, `notifications/${notificationId}`, accessToken);
    }

    public async getNotificationsByUserId(userId: number, includeNewOnly: boolean, page: number, pageSize: number, accessToken: string): Promise<ServiceResponse<PagedResults<NotificationDto>>> {
        return await super.Get<PagedResults<NotificationDto>>(this.baseUrl, `users/${userId}/notifications?includeNewOnly=${includeNewOnly}&page=${page}&pageSize=${pageSize}`, accessToken);
    }

    public async setNotificationStatus(notificationId: string, readStatus: ReadStatus, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.PutNoResponse(this.baseUrl, `notifications/${notificationId}/status/${readStatus}`, null, accessToken);
    }

    public async deleteNotification(notificationId: string, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Delete(this.baseUrl, `notifications/${notificationId}`, accessToken);
    }        

    public async sendEmail(dto: SendEmailDto, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.PostNoResponse<SendEmailDto>(this.baseUrl, `emails`, dto, accessToken);
    }

    public async sendSms(dto: SendSmsDto, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.PostNoResponse<SendSmsDto>(this.baseUrl, `sms`, dto, accessToken);
    }

    public async sendVoiceMessage(dto: SendVoiceMessageDto, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.PostNoResponse<SendVoiceMessageDto>(this.baseUrl, `voice-messages`, dto, accessToken);
    }
}

export { CommsClient }